import {
  formatSendMoney,
  formatSendMoneyWithCode,
} from "@/modules/dashboard/dispersions/components/WalletSend/formatSendMoney";
import { formatRateLine } from "@/modules/dashboard/dispersions/utils/walletSend";
import { CurrencyFlag } from "@/modules/dashboard/wallets/components/CurrencyFlag";
import { ColocaCountUp } from "@/shared/components/motion/ColocaCountUp";
import { ColocaSuccessMark } from "@/shared/components/motion/ColocaSuccessMark";
import useTranslations from "@/shared/lang/useTranslations";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";

import type { NativeSwap, SwapWallet } from "../../services/swap.service";

export interface SwapSuccessProps {
  swap: NativeSwap;
}

/**
 * The conversion done: what landed, at what rate, and both balances as they are now. The balances come from
 * the swap's own answer, which the backend reads when it responds, so they are today's figures.
 */
export function SwapSuccess({ swap }: SwapSuccessProps) {
  const { t, activeLanguage } = useTranslations("swaps");
  const { summary, wallets } = swap;
  const to = summary.destination_currency;
  const received = Number(summary.amount_received);
  const digits = minorUnitsFor(to);
  const net = Number(summary.net_converted);
  const rate =
    net > 0 && received > 0
      ? received / net >= 1
        ? { base: summary.origin_currency, quote: to, value: received / net }
        : { base: to, quote: summary.origin_currency, value: net / received }
      : null;

  const balanceRow = (wallet: SwapWallet, delta: string, sign: "−" | "+") => (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <span className="flex items-center gap-2 text-sm text-foreground">
        <CurrencyFlag currency={wallet.currency} />
        {t("convert_wallet", { currency: wallet.currency })}
      </span>
      <span className="text-right">
        <span className="block font-mono text-sm font-semibold text-foreground tabular-nums">
          {formatSendMoneyWithCode(wallet.balance.available, wallet.currency, activeLanguage)}
        </span>
        <span
          className={`block font-mono text-xs tabular-nums ${sign === "+" ? "text-success" : "text-muted-foreground"}`}
        >
          {sign} {formatSendMoney(delta, wallet.currency, activeLanguage)}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex flex-col items-center gap-5 px-2 pt-6 pb-2 text-center">
      <ColocaSuccessMark className="size-16" />
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-foreground">
          {t("convert_success_title", {
            amount: formatSendMoneyWithCode(summary.total_debited, summary.origin_currency, activeLanguage),
          })}
        </h2>
        <p className="pt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("convert_success_landed", { currency: to })}
        </p>
        <p className="font-mono text-3xl font-bold tracking-tight text-foreground tabular-nums">
          <ColocaCountUp
            value={received}
            format={(value) => formatSendMoney(value.toFixed(digits), to, activeLanguage)}
          />
          <span className="ml-2 text-sm font-medium tracking-wider text-muted-foreground">{to}</span>
        </p>
        {rate && (
          <p className="text-sm text-muted-foreground">
            {t("convert_success_rate", { rate: formatRateLine(rate, activeLanguage) })}
          </p>
        )}
      </div>
      <section className="w-full rounded-xl border border-border bg-card px-4 text-left">
        <h3 className="pt-3 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("convert_balance_now")}
        </h3>
        <div className="divide-y divide-border">
          {balanceRow(wallets.origin, summary.total_debited, "−")}
          {balanceRow(wallets.destination, summary.amount_received, "+")}
        </div>
      </section>
    </div>
  );
}
